import './index.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { Layout } from './components/Layout/Layout';
import { LoginPage } from './pages/Login';
import { DashboardPage } from './pages/Dashboard';
import { NodesPage } from './pages/Nodes';
import { BillingPage } from './pages/Billing';
import { CodeRepositoriesPage } from './pages/CodeRepositories';
import { SessionsPage } from './pages/Sessions';
import { ApiKeysPage } from './pages/ApiKeys';
import { TestPage } from './pages/Test';
import { ChatPage } from './pages/Chat';
import { AdminUsersPage } from './pages/AdminUsers';
import { AdminPlansPage } from './pages/AdminPlans';
import { PromptForgePage } from './pages/PromptForge';
import { SwarmV2Page } from './pages/SwarmV2';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isLoggedIn, loading } = useAuth();
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: '16px' }}>
      <div className="spinner" style={{ width: 32, height: 32 }} />
      <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading Erix Life...</span>
    </div>
  );
  return isLoggedIn ? <>{children}</> : <Navigate to="/login" replace />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return user.role === 'admin' ? <>{children}</> : <Navigate to="/chat" replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<Layout />}>
            <Route index element={<Navigate to="/chat" replace />} />
            <Route path="chat" element={<ChatPage />} />
            
            <Route path="dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
            <Route path="billing" element={<PrivateRoute><BillingPage /></PrivateRoute>} />
            <Route path="api-keys" element={<PrivateRoute><ApiKeysPage /></PrivateRoute>} />
            <Route path="sessions" element={<PrivateRoute><SessionsPage /></PrivateRoute>} />

            {/* Admin Only Routes */}
            <Route path="nodes" element={<AdminRoute><NodesPage /></AdminRoute>} />
            <Route path="test" element={<AdminRoute><TestPage /></AdminRoute>} />
            <Route path="projects" element={<AdminRoute><CodeRepositoriesPage /></AdminRoute>} />
            <Route path="admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
            <Route path="admin/plans" element={<AdminRoute><AdminPlansPage /></AdminRoute>} />
            <Route path="admin/prompt-forge" element={<AdminRoute><PromptForgePage /></AdminRoute>} />
            <Route path="admin/swarm-v2" element={<AdminRoute><SwarmV2Page /></AdminRoute>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode><App /></StrictMode>
);
